import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://batiassist.com';
const DEFAULT_OG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/4e491698-f834-49a8-a1e5-8fd3efa88feb/images/043be2910df269f7c8b366d9b9c15ae412a6a24a8e39741e858abc967a1cbc8f.png';
const SITE_NAME = 'BatiAssist';
const DEFAULT_DESCRIPTION = "BatiAssist : L'assistant digital des artisans du bâtiment. CRM, devis, factures, gestion de chantiers et agents IA pour plombiers, électriciens, maçons et tous les professionnels du BTP.";

// SEO Configuration for each page
export const SEO_CONFIG = {
  home: {
    title: "BatiAssist | L'Assistant Digital des Artisans du Bâtiment",
    description: "Transforme ton entreprise artisanale avec BatiAssist : CRM bâtiment, gestion de devis et factures, suivi de chantiers, agents IA et assistants humains. Solution tout-en-un pour plombiers, électriciens, maçons.",
    keywords: "CRM bâtiment, assistant artisan, gestion chantier, devis facture BTP, logiciel plombier, logiciel électricien, logiciel maçon, gestion entreprise bâtiment, automatisation BTP",
    path: "/",
    type: "website"
  },
  features: {
    title: "Fonctionnalités | CRM & Gestion Complète pour Artisans - BatiAssist",
    description: "Découvrez toutes les fonctionnalités de BatiAssist : CRM intégré, devis et factures automatisés, planning chantiers, pointage GPS, agents IA pour réseaux sociaux, et bien plus.",
    keywords: "fonctionnalités CRM bâtiment, gestion devis automatique, facturation artisan, planning chantier, pointage géolocalisation, agent IA réseaux sociaux",
    path: "/fonctionnalites",
    type: "website"
  },
  pricing: {
    title: "Tarifs | Offres Adaptées aux Artisans du Bâtiment - BatiAssist",
    description: "Choisis l'offre BatiAssist adaptée à ton activité : Start à 149€/mois, Assistant Mi-Temps à 499€/mois, Assistant Dédié à 949€/mois ou Croissance à 1499€/mois.",
    keywords: "prix CRM bâtiment, tarif logiciel artisan, coût assistant virtuel, abonnement gestion chantier, offre BatiAssist",
    path: "/tarifs",
    type: "website"
  },
  contact: {
    title: "Contact | Parlons de Ton Projet - BatiAssist",
    description: "Contacte l'équipe BatiAssist pour un diagnostic gratuit de ta transition digitale. Réserve un appel avec Romain Trallero, fondateur de BatiAssist.",
    keywords: "contact BatiAssist, diagnostic digital artisan, rendez-vous BatiAssist, démonstration CRM bâtiment",
    path: "/contact",
    type: "website"
  },
  blog: {
    title: "Blog | Conseils & Actualités pour Artisans du Bâtiment - BatiAssist",
    description: "Retrouvez nos articles, conseils et actualités pour les artisans du bâtiment : digitalisation, gestion d'entreprise, marketing, productivité et bien plus.",
    keywords: "blog artisan bâtiment, conseils gestion entreprise BTP, actualités construction, digitalisation artisan",
    path: "/blog",
    type: "blog"
  },
  diagnostic: {
    title: "Diagnostic Gratuit | Évalue Ta Transition Digitale - BatiAssist",
    description: "Fais le diagnostic gratuit de ton entreprise artisanale et découvre comment BatiAssist peut t'aider à gagner du temps et développer ton activité.",
    keywords: "diagnostic digital gratuit, évaluation entreprise artisan, transition numérique BTP, audit gestion chantier",
    path: "/diagnostic",
    type: "website"
  },
  assistant: {
    title: "Nos Assistants | Délégation Administrative pour Artisans - BatiAssist",
    description: "Découvre nos assistants dédiés aux artisans du bâtiment : gestion administrative, relances clients, planification, et support quotidien pour te libérer du temps.",
    keywords: "assistant administratif artisan, délégation tâches BTP, secrétaire virtuelle bâtiment, aide gestion entreprise",
    path: "/nos-assistants",
    type: "website"
  },
  crm: {
    title: "CRM Bâtiment | Gestion Clients & Chantiers Simplifiée - BatiAssist",
    description: "Le CRM spécialisé pour les artisans du bâtiment : gestion prospects, suivi clients, pipeline commercial, historique interventions et bien plus.",
    keywords: "CRM bâtiment, gestion client artisan, pipeline commercial BTP, suivi prospect construction, logiciel relation client",
    path: "/crm-batiment",
    type: "website"
  }
};

// Main SEO Component
export default function SEO({ 
  page = 'home',
  title,
  description,
  keywords,
  image,
  path,
  type = 'website',
  article = null, // For blog articles: { publishedTime, modifiedTime, author, tags }
  noindex = false
}) {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  
  const seoTitle = title || config.title;
  const seoDescription = description || config.description;
  const seoKeywords = keywords || config.keywords;
  const seoImage = image || DEFAULT_OG_IMAGE;
  const seoPath = path || config.path;
  const seoType = type || config.type;
  const canonicalUrl = `${SITE_URL}${seoPath}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content={seoType === 'blog' ? 'article' : 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} - ${seoTitle}`} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} - ${seoTitle}`} />
      
      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.tags && article.tags.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Additional SEO tags */}
      <meta name="author" content="BatiAssist - Romain Trallero" />
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="France" />
      <meta name="language" content="French" />
      <meta name="revisit-after" content="7 days" />
    </Helmet>
  );
}

// Structured Data Component for JSON-LD
export function StructuredData({ type, data }) {
  let structuredData = null;

  switch (type) {
    case 'organization':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "BatiAssist",
        "url": SITE_URL,
        "logo": DEFAULT_OG_IMAGE,
        "description": DEFAULT_DESCRIPTION,
        "founder": {
          "@type": "Person",
          "name": "Romain Trallero",
          "jobTitle": "Fondateur"
        },
        "foundingDate": "2024",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "FR"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "sales",
          "url": "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min"
        },
        "sameAs": [
          "https://www.linkedin.com/company/batiassist",
          "https://www.facebook.com/batiassist"
        ]
      };
      break;

    case 'software':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "BatiAssist",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "url": SITE_URL,
        "description": DEFAULT_DESCRIPTION,
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "149",
          "highPrice": "1499",
          "priceCurrency": "EUR",
          "offerCount": "4"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "150"
        }
      };
      break;

    case 'service':
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "CRM et assistant digital pour artisans",
        "provider": {
          "@type": "Organization",
          "name": "BatiAssist"
        },
        "areaServed": {
          "@type": "Country",
          "name": "France"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Offres BatiAssist",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": "Offre Start" },
              "price": "149",
              "priceCurrency": "EUR"
            },
            {
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": "Offre Assistant Mi-Temps" },
              "price": "499",
              "priceCurrency": "EUR"
            },
            {
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": "Offre Assistant Dédié" },
              "price": "949",
              "priceCurrency": "EUR"
            },
            {
              "@type": "Offer",
              "itemOffered": { "@type": "Service", "name": "Offre Croissance" },
              "price": "1499",
              "priceCurrency": "EUR"
            }
          ]
        }
      };
      break;

    case 'article':
      if (data) {
        structuredData = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.title,
          "description": data.description,
          "image": data.image || DEFAULT_OG_IMAGE,
          "datePublished": data.publishedTime,
          "dateModified": data.modifiedTime || data.publishedTime,
          "author": {
            "@type": "Person",
            "name": data.author || "Romain Trallero"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BatiAssist",
            "logo": {
              "@type": "ImageObject",
              "url": DEFAULT_OG_IMAGE
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": data.url
          }
        };
      }
      break;

    case 'faq':
      if (data && data.questions) {
        structuredData = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.questions.map(q => ({
            "@type": "Question",
            "name": q.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": q.answer
            }
          }))
        };
      }
      break;

    case 'breadcrumb':
      if (data && data.items) {
        structuredData = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": data.items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": `${SITE_URL}${item.path}`
          }))
        };
      }
      break;

    default:
      return null;
  }

  if (!structuredData) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}

// Helper component combining SEO + StructuredData
export function PageSEO({ page, structuredDataTypes = [], ...props }) {
  return (
    <>
      <SEO page={page} {...props} />
      {structuredDataTypes.map((type, i) => (
        <StructuredData key={i} type={type} data={props.structuredData} />
      ))}
    </>
  );
}
